import cookies from 'js-cookie';

import { ExceptionDimensions, ResponseCode, getIsLocalDev, getIsStorageAvailable, logAnalyticsEvent } from '@vestico/utils';

import { DocumentID, ProductInfo, ProductResponse, RawProductsResponse, WidgetStylesConfiguration, defaultCookieAttributes } from '@vestico/model';

import { AuthorizedRequestInfo, ProductApi, ProductConfiguration, ProductResponseRetry } from '../model/api';
import { AbstractApiClient } from './apiClient';
import { createAPIRequestURL, createProductRequest, fetchRequest, getIsInternal } from './helpers';

const vesticoStylesKey = 'co.vestico.styles';

const VESTICO_WIDGET_STYLES_EXPIRATION_TIME_HOURS = 3.0;

interface VesticoWidgetStylesStorageItem {
  fetchTimestamp: number;
  styles: WidgetStylesConfiguration;
}

export class ProductApiClient extends AbstractApiClient implements ProductApi {
  public async fetchProduct(
    productConfig: ProductConfiguration | ProductConfiguration[],
    userUID: string | undefined,
    authRequestInfo: AuthorizedRequestInfo,
    isRetry: boolean
  ): Promise<ProductResponse | null | typeof ProductResponseRetry> {
    const fetchURL = await createProductRequest(
      productConfig,
      this.clientId,
      userUID,
      authRequestInfo,
      this.isPreview,
      !!this.sdkAPIKey,
      this.clientExperiments
    );

    if (!fetchURL) {
      return null;
    }

    try {
      const result = await fetchRequest({ url: fetchURL, iOSBundleIdentifier: this.iOSBundleIdentifier, authRequestInfo });

      const statusCode = result.status;
      if (statusCode === ResponseCode.widgetDisabled) {
        console.info(`Widget disabled`);
        return null;
      }

      const refetchAuthInfo = statusCode === ResponseCode.unauthorized && !isRetry;
      if (refetchAuthInfo) {
        return ProductResponseRetry;
      }

      if (statusCode !== ResponseCode.success) {
        console.error(`Product request returned error ${result.status}`);
        const message = await result.text();
        console.debug(`Result text:\n${message}`);
        if (statusCode !== ResponseCode.noProduct) {
          logAnalyticsEvent({
            category: 'exception',
            event: `fetch product`,
            name: `${result.status}`,
            dimensions: { provider: this.clientId, description: message } as ExceptionDimensions,
          });
        }
        return null;
      }

      const fetchedProduct = (await result.json()) as ProductResponse;
      return fetchedProduct;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `product req`,
        name: error.message,
      });
      return null;
    }
  }

  public async fetchRawProducts(
    productDocumentIDs: DocumentID[],
    providerDocumentID: DocumentID,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<RawProductsResponse> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/products');

    if (!fetchURL) {
      return null;
    }

    try {
      fetchURL.searchParams.append('provider', providerDocumentID);

      for (const productDocumentID of productDocumentIDs) {
        fetchURL.searchParams.append('products', productDocumentID);
      }

      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'GET',
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      const statusCode = result.status;
      if (statusCode !== ResponseCode.success) {
        console.error(`Raw Product request returned error ${result.status}`);
        const message = await result.text();
        console.debug(`Result text:\n${message}`);
        logAnalyticsEvent({
          category: 'exception',
          event: `fetch-raw-product`,
          name: `${result.status}`,
          dimensions: { provider: this.clientId, description: message } as ExceptionDimensions,
        });
        return null;
      }

      const fetchedProducts = (await result.json()) as ProductInfo[];
      return fetchedProducts;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `raw product req`,
        name: error.message,
      });
      return null;
    }
  }

  public async fetchWidgetStyles(authRequestInfo: AuthorizedRequestInfo): Promise<WidgetStylesConfiguration | null> {
    const isInternal = getIsInternal();

    const isStorageAvailable = getIsStorageAvailable();
    const loadWidgetStylesFromStorage = () => {
      const rawItem = isStorageAvailable ? localStorage.getItem(vesticoStylesKey) : cookies.get(vesticoStylesKey);
      const item: VesticoWidgetStylesStorageItem = rawItem ? JSON.parse(rawItem) : null;
      if (item && Date.now() < item.fetchTimestamp + VESTICO_WIDGET_STYLES_EXPIRATION_TIME_HOURS * 1000 * 3600) {
        console.debug('Returning cached styles');
        return item.styles;
      }

      return null;
    };

    const storeWidgetStylesInStorage = (styles: WidgetStylesConfiguration) => {
      if (!styles) {
        return;
      }
      const storageItem: VesticoWidgetStylesStorageItem = { fetchTimestamp: Date.now(), styles };
      const storageItemString = JSON.stringify(storageItem);
      if (isStorageAvailable) {
        localStorage.setItem(vesticoStylesKey, storageItemString);
      } else {
        const attributes = defaultCookieAttributes();
        attributes.expires = VESTICO_WIDGET_STYLES_EXPIRATION_TIME_HOURS / 24;
        cookies.set(vesticoStylesKey, storageItemString, attributes);
      }
    };

    // Load local widget styles upfront
    if (!this.isPreview && !isInternal && !getIsLocalDev()) {
      const styles = loadWidgetStylesFromStorage();
      if (styles) {
        return styles;
      }
    }

    const fetchURL = await createAPIRequestURL(authRequestInfo, '/styles');

    if (!fetchURL) {
      return null;
    }

    try {
      fetchURL.searchParams.append('client', this.clientId);

      if (this.isPreview) {
        fetchURL.searchParams.append('isPreview', 'true');
      }

      if (isInternal) {
        fetchURL.searchParams.append('isInternal', 'true');
      }

      if (this.isSDK) {
        fetchURL.searchParams.append('isSDK', 'true');
      }

      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'GET',
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      const statusCode = result.status;
      if (statusCode !== ResponseCode.success) {
        console.error(`Styles request returned error ${result.status}`);
        const message = await result.text();
        logAnalyticsEvent({
          category: 'exception',
          event: `fetch-styles`,
          name: `${result.status}`,
          dimensions: { provider: this.clientId, description: message } as ExceptionDimensions,
        });
        return null;
      }

      const fetchedStyles = (await result.json()) as WidgetStylesConfiguration;
      storeWidgetStylesInStorage(fetchedStyles);
      return fetchedStyles;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `fetch-styles-req`,
        name: error.message,
      });
      return null;
    }
  }
}
